import React, {
    FC, useCallback, useEffect, useState,
} from 'react';

import {
    Button, Input, message, Modal,
} from 'antd';

import { t } from 'i18next';
import { useLocation } from 'react-router';
import { API } from 'aws-amplify';
import { MFA_ATTEMPTS } from '@/consts';
import AttemptsRemainWarning from '@/components/OpenViewer/AttemptsRemainWarning';
import { OpenOptions } from '@/config/openOptions';
import { batchActionsNamespace } from '@/components/MyFiles/MyFilesHeader/ExtraContent/nameSpace';
import { useStores } from '@/components/hooks';
import i18n from '@/content';
import { BASEURL, ENDPOINTS } from '@/api';
import appConfig from '@/config/env';
import Spinner from '@/components/Common/Spin';

const MESSAGE_DURATION_SECONDS = 8;

interface MFAPasswordModalProps {
    onSuccessAfterVerify?: () => void | Promise<void>;
    onAttemptsExhausted?: () => void;
}

const MFAPasswordModal: FC<MFAPasswordModalProps> = ({ onSuccessAfterVerify, onAttemptsExhausted }) => {
    const {
        filesAccessStore,
        batchDownloadStore,
        filesListStore,
        userStore,
    } = useStores();
    const location = useLocation();
    const {
        batchDownloadAttemptsCounter,
        tryFetchAccessLink,
        tryStartBatchDownload,
    } = filesAccessStore;
    const {
        setMFARequiredFilesList,
        downloadsList,
        setIsMFAPasswordVisible,
        setIsMFAPasswordMounted,
        isMFAPasswordVisible,
        lastMFASource,
    } = batchDownloadStore;
    const { filesToDisplay } = filesListStore;

    const [password, setPassword] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [passwordHint, setPasswordHint] = useState<string | null>(null);
    const [isHintLoading, setIsHintLoading] = useState<boolean>(false);
    const [localAttemptsCounter, setLocalAttemptsCounter] = useState<number>(MFA_ATTEMPTS);

    // Reset attempts counter when modal opens
    useEffect(() => {
        if (isMFAPasswordVisible) {
            setLocalAttemptsCounter(MFA_ATTEMPTS);
            setPassword('');
        }
    }, [isMFAPasswordVisible]);

    useEffect(() => {
        const fetchPasswordHint = async (): Promise<void> => {
            try {
                const userIdentifier = userStore.currentUserEmail;
                if (!userIdentifier) {
                    return;
                }
                setIsHintLoading(true);
                const response = await API.get(
                    BASEURL.backend(),
                    ENDPOINTS.getUserPassword(userIdentifier),
                    {},
                );
                const hintFromBackend = response?.hint;
                const finalHint = hintFromBackend
                    || appConfig.PASSWORD_DEFAULT_HINT
                    || null;
                setPasswordHint(finalHint);
            } catch (error) {
                console.error('Error checking password availability for MFA download:', error);
                const fallbackHint = appConfig.PASSWORD_DEFAULT_HINT || null;
                setPasswordHint(fallbackHint);
            } finally {
                setIsHintLoading(false);
            }
        };

        fetchPasswordHint();
    }, [userStore.currentUserEmail]);

    const closeMFAPassword = useCallback((autoClosing: boolean = false): void => {
        if (!autoClosing) {
            const { MFARequiredFilesList, updateBatchDownloadStatusWithError } = batchDownloadStore;
            const filesData = filesToDisplay.filter((item) => MFARequiredFilesList.includes(item.file_id));
            filesData.forEach((item) => {
                updateBatchDownloadStatusWithError(item.file_id, item.filename);
            });
        }
        setMFARequiredFilesList([]);
        setIsMFAPasswordVisible(false);
        setIsMFAPasswordMounted(false);
    }, [batchDownloadStore, filesToDisplay, setMFARequiredFilesList, setIsMFAPasswordMounted, setIsMFAPasswordVisible]);

    const onClickBatchLoading = async (mfaPassword?: string): Promise<void> => {
        const { MFARequiredFilesList } = batchDownloadStore;
        const currentPath = location.pathname;
        const isSharedWithMe = currentPath === '/shared-with-me';
        const filesData = filesToDisplay.filter((item) => MFARequiredFilesList.includes(item.file_id));
        const anyFolders = filesData.some((item) => item.is_folder);
        const isDownloadingWorkspaceItself = filesData.length === 0 && MFARequiredFilesList.length !== 0;

        const hasViewerSuccessHandler = !!onSuccessAfterVerify;
        const { lastMFAPasswordContext } = filesAccessStore;
        const isExternalAppContext = !!lastMFAPasswordContext
            && (lastMFAPasswordContext.accessType === OpenOptions.office365
                || lastMFAPasswordContext.accessType === OpenOptions.googledrive);
        const hasCustomSuccessHandler = hasViewerSuccessHandler || isExternalAppContext;

        let isSucceed = false;
        try {
            if (hasCustomSuccessHandler) {
                const response = await API.post(
                    BASEURL.backend(),
                    ENDPOINTS.verifyFileAccess(),
                    {
                        body: { method: 'password', attempt: mfaPassword },
                    },
                );

                if (!response?.success) {
                    throw new Error('MFA password verification failed');
                }

                if (hasViewerSuccessHandler) {
                    await onSuccessAfterVerify?.();
                } else if (isExternalAppContext && lastMFAPasswordContext) {
                    const { key: fileIdToOpen, accessType } = lastMFAPasswordContext;
                    const fileData = filesToDisplay.find((item) => item.file_id === fileIdToOpen);
                    const filename = fileData?.filename || fileIdToOpen;

                    await tryFetchAccessLink({
                        fileId: fileIdToOpen,
                        filename,
                        accessType: accessType as OpenOptions,
                    });

                    const accessState = filesAccessStore.getAccessTypeState(fileIdToOpen, accessType);
                    const link = (accessState?.result as any)?.link;
                    if (link) {
                        const newWin = window.open(link);
                        if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
                            window.location.href = link;
                        }
                    }
                }

                closeMFAPassword(true);
                return;
            }

            // Standard download / batch-download flows.
            if (isSharedWithMe) {
                const response = await API.post(
                    BASEURL.backend(),
                    ENDPOINTS.verifyFileAccess(),
                    {
                        body: { method: 'password', attempt: mfaPassword },
                    },
                );

                if (!response?.success) {
                    throw new Error('MFA password verification failed');
                }
            }

            if (!MFARequiredFilesList.length) {
                message.info(t(`${batchActionsNamespace}.noFiles`), MESSAGE_DURATION_SECONDS);
            } else if (isSharedWithMe && lastMFASource === 'batch') {
                // True batch-download flow on shared-with-me: MFA password was required for a multi-file batch.
                // After verifying via verifyFileAccess, re-start batch download without passing the password again.
                isSucceed = await tryStartBatchDownload(MFARequiredFilesList);
            } else if (isDownloadingWorkspaceItself || anyFolders) {
                isSucceed = await tryStartBatchDownload(MFARequiredFilesList, mfaPassword);
            } else {
                const foundItem = downloadsList.find((item) => item.id === MFARequiredFilesList[0]);
                const name = foundItem?.name || MFARequiredFilesList[0];
                isSucceed = await tryFetchAccessLink({
                    fileId: MFARequiredFilesList[0],
                    filename: name,
                    accessType: OpenOptions.download,
                    code: mfaPassword,
                });
            }

            if (isSucceed) {
                closeMFAPassword(true);
            }
        } catch (e) {
            message.error(t('recipientPage.errors.notAuthorisedPassword'), MESSAGE_DURATION_SECONDS);
            setPassword(''); // Clear password for retry
            if (localAttemptsCounter > 1) {
                setLocalAttemptsCounter((prev) => prev - 1);
            } else {
                onAttemptsExhausted?.();
                closeMFAPassword(true);
            }
        }
    };

    const handleVerifyClick = useCallback(async (): Promise<void> => {
        if (!password) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onClickBatchLoading(password);
        } finally {
            setIsSubmitting(false);
        }
    }, [password, onClickBatchLoading]);

    return (
        <Modal
            open={isMFAPasswordVisible}
            centered
            footer={null}
            onCancel={() => closeMFAPassword(false)}
            maskClosable={false}
        >
            <Spinner spinning={isSubmitting || (isHintLoading && !password && !!userStore.currentUserEmail)}>
                {localAttemptsCounter < MFA_ATTEMPTS && (
                    <AttemptsRemainWarning attemptsCounter={localAttemptsCounter} />
                )}
                <div>
                    <h3>{i18n.t('mfaPasswordModal.title')}</h3>
                    {appConfig.USE_SIMPLIFIED_PASSWORD_PROMPT ? (
                        <div style={{ marginBottom: 8, textAlign: 'left' }}>
                            {i18n.t('recipientPage.OTPSteps.simplifiedPasswordPrompt')}
                        </div>
                    ) : passwordHint && (
                        <div style={{ marginBottom: 8, textAlign: 'left' }}>
                            <div style={{ fontSize: 12, opacity: 0.7 }}>
                                {i18n.t('recipientPage.OTPSteps.hintLabel')}
                            </div>
                            <div style={{ fontSize: 14 }}>
                                {passwordHint}
                            </div>
                        </div>
                    )}
                    <Input.Password
                        autoFocus
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder={i18n.t('mfaPasswordModal.placeholder')}
                        autoComplete="off"
                    />
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Button
                            type="primary"
                            onClick={handleVerifyClick}
                            loading={isSubmitting}
                            disabled={!password}
                        >
                            {i18n.t('general.buttons.confirm')}
                        </Button>
                    </div>
                </div>
            </Spinner>
        </Modal>
    );
};

export default MFAPasswordModal;
